import { useEffect, useMemo, useState } from "react";
import { api, errorMsg } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Search, Plus, Check } from "lucide-react";

const norm = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

export function SelectorModeloDialog({ abierto, onCerrar, modelos = [], onSeleccionar,
  titulo = "Asignar modelo", descripcion, guardando = false }) {
  const [q, setQ] = useState("");
  const [sel, setSel] = useState("");
  const [crear, setCrear] = useState(false);
  const [nuevo, setNuevo] = useState({ marca: "HONDA", modelo: "", anio_inicio: "", anio_fin: "" });
  const [creando, setCreando] = useState(false);

  useEffect(() => {
    if (!abierto) { setQ(""); setSel(""); setCrear(false); setNuevo({ marca: "HONDA", modelo: "", anio_inicio: "", anio_fin: "" }); }
  }, [abierto]);

  const filtrados = useMemo(() => {
    const t = norm(q).split(" ").filter(Boolean);
    return modelos.filter((m) => {
      const txt = norm(`${m.marca} ${m.modelo} ${m.nombre_mostrar || ""} ${m.anio_inicio || ""} ${m.anio_fin || ""}`);
      return t.every((w) => txt.includes(w));
    }).slice(0, 200);
  }, [q, modelos]);

  const confirmarExistente = () => {
    const m = modelos.find((x) => x.id === sel);
    if (m) onSeleccionar?.(m);
  };

  const confirmarNuevo = async () => {
    if (!nuevo.marca.trim() || !nuevo.modelo.trim()) { toast.error("Marca y modelo son obligatorios"); return; }
    setCreando(true);
    try {
      const payload = {
        marca: nuevo.marca.trim(), modelo: nuevo.modelo.trim(),
        anio_inicio: nuevo.anio_inicio ? Number(nuevo.anio_inicio) : null,
        anio_fin: nuevo.anio_fin ? Number(nuevo.anio_fin) : null,
      };
      const { data } = await api.post("/modelos", payload);
      toast.success("Modelo creado");
      onSeleccionar?.(data, true);
    } catch (e) { toast.error(errorMsg(e)); }
    finally { setCreando(false); }
  };

  return (
    <Dialog open={abierto} onOpenChange={(o) => { if (!o) onCerrar?.(); }}>
      <DialogContent data-testid="selector-modelo-dialog" className="max-w-lg max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{titulo}</DialogTitle>
          {descripcion && <DialogDescription>{descripcion}</DialogDescription>}
        </DialogHeader>

        {!crear ? (
          <>
            <div className="relative">
              <Search size={15} className="absolute left-2.5 top-2.5 text-slate-400" />
              <Input data-testid="selector-modelo-buscar" value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Buscar por marca, modelo o año…" className="pl-8" />
            </div>
            <div className="flex-1 overflow-y-auto border border-slate-200 rounded-md divide-y divide-slate-100 min-h-[120px]">
              {filtrados.map((m) => (
                <label key={m.id} data-testid={`selector-modelo-opt-${m.id}`}
                  className={`flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50 ${sel === m.id ? "bg-indigo-50" : ""}`}>
                  <input type="radio" name="modelo-sel" checked={sel === m.id} onChange={() => setSel(m.id)}
                    className="accent-indigo-600 w-4 h-4" />
                  <span className="text-slate-700">{m.nombre_mostrar || `${m.marca} ${m.modelo}`}</span>
                </label>
              ))}
              {filtrados.length === 0 && (
                <p className="text-sm text-slate-400 py-6 text-center" data-testid="selector-modelo-vacio">
                  Sin coincidencias. Podés crear el modelo nuevo.
                </p>
              )}
            </div>
            <div className="flex items-center justify-between pt-2 border-t border-slate-100">
              <Button variant="outline" size="sm" data-testid="selector-modelo-crear-toggle"
                className="border-teal-500 text-teal-600 hover:bg-teal-50" onClick={() => setCrear(true)}>
                <Plus size={14} className="mr-1" /> Crear modelo nuevo
              </Button>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={() => onCerrar?.()}>Cancelar</Button>
                <Button size="sm" data-testid="selector-modelo-confirmar" onClick={confirmarExistente}
                  disabled={!sel || guardando}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-40">
                  {guardando ? "Asignando…" : "Asignar este modelo"}
                </Button>
              </div>
            </div>
          </>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-xs font-medium text-slate-600">Marca *</label>
                <Input data-testid="selector-modelo-nuevo-marca" value={nuevo.marca}
                  onChange={(e) => setNuevo((n) => ({ ...n, marca: e.target.value }))} placeholder="Ej: HONDA" />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-slate-600">Modelo *</label>
                <Input data-testid="selector-modelo-nuevo-modelo" value={nuevo.modelo}
                  onChange={(e) => setNuevo((n) => ({ ...n, modelo: e.target.value }))} placeholder="Ej: CB160F HORNET" />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-slate-600">Año inicio (opcional)</label>
                <Input data-testid="selector-modelo-nuevo-anio-inicio" inputMode="numeric" maxLength={4} value={nuevo.anio_inicio}
                  onChange={(e) => setNuevo((n) => ({ ...n, anio_inicio: e.target.value.replace(/\D/g, "").slice(0, 4) }))} placeholder="Ej: 2016" />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-slate-600">Año fin (vacío = actual)</label>
                <Input data-testid="selector-modelo-nuevo-anio-fin" inputMode="numeric" maxLength={4} value={nuevo.anio_fin}
                  onChange={(e) => setNuevo((n) => ({ ...n, anio_fin: e.target.value.replace(/\D/g, "").slice(0, 4) }))} placeholder="Ej: 2020" />
              </div>
            </div>
            <div className="flex items-center justify-between pt-2 border-t border-slate-100">
              <Button variant="ghost" size="sm" onClick={() => setCrear(false)}>← Volver a la lista</Button>
              <Button size="sm" data-testid="selector-modelo-nuevo-guardar" onClick={confirmarNuevo}
                disabled={creando || guardando || !nuevo.marca.trim() || !nuevo.modelo.trim()}
                className="bg-teal-600 hover:bg-teal-700 text-white disabled:opacity-40">
                <Check size={14} className="mr-1" /> {creando ? "Creando…" : "Crear y asignar"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
